import React, { useState, useMemo, useCallback } from 'react';
import Modal from 'pages/components/Modal/Modal';
import CustomButton from 'pages/components/CustomButton/CustomButton';
import HeroSection from '../components/HeroSection';
import ControllerFeatures from '../components/ControllerFeatures';
import DownloadComponent from '../components/DownloadComponent';
import WhereToBuy from 'pages/OurDrones/WhereToBuy/WhereToBuy';
import DroneSpecifications from 'pages/OurDrones/DroneSpecification/DroneSpecification';
import BVLOSOperations from 'pages/OurDrones/BVLOSOperations/BVLOSOperations';
import Certifications from 'pages/OurDrones/Certifications/Certifications';
import { useApi } from 'hooks/useApi';
import styles from './AxlOrbit.module.css';

const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

const AxlOrbit = () => {
  const [isWhereToBuyModalOpen, setIsWhereToBuyModalOpen] = useState(false);

  const fallbackImages = useMemo(() => ({
    orbitMain: '/Assets/Images/axlOrbit/orbitMain.svg',
    orbitSide: '/Assets/Images/axlOrbit/orbitMain.svg',
    orbitTop: '/Assets/Images/axlOrbit/orbit-top.svg',
    orbitAngle: '/Assets/Images/axlOrbit/orbit-center.svg',
    orbitBottom: '/Assets/Images/axlOrbit/orbit-bottom.svg'
  }), []);

  const { data: productData, loading: productLoading, error: productError } = useApi('/product-types', { params: { slug: 'axl-orbit' } });
  const { data: specificationsResponse, loading: specsLoading, error: specsError } = useApi('/products', { params: { slug: 'axl-orbit' } });

  const specificationsData = useMemo(() => {
    if (!specificationsResponse?.success || !specificationsResponse?.products) return [];
    return specificationsResponse.products.map(product => ({
      ...product,
      specifications: product.specification?.map(spec => ({ label: spec.name, value: spec.value })) || []
    }));
  }, [specificationsResponse]);

  const getFeaturesData = useMemo(() => {
    if (!productData?.success || !productData?.products?.features) return [];
    return productData.products.features.map(item => ({ icon: item.icon, label: item.title }));
  }, [productData]);

  const getBVLOSOperationsData = useMemo(() => {
    if (!productData?.success || !productData?.products?.designed_for) return [];
    return productData.products.designed_for.map(item => ({ icon: item.icon, label: item.title, image: item.image }));
  }, [productData]);

  const getAdditionalImages = useMemo(() => {
    if (!productData?.success) {
      return [
        { src: fallbackImages.orbitTop, alt: 'Orbit Top View' },
        { src: fallbackImages.orbitAngle, alt: 'Orbit Angle View' },
        { src: fallbackImages.orbitBottom, alt: 'Orbit Bottom View' }
      ];
    }

    const images = [];
    const product = productData.products;
    if (product?.image1) images.push({ src: product.image1, alt: 'Orbit View 1' });
    if (product?.image2) images.push({ src: product.image2, alt: 'Orbit View 2' });
    if (product?.image3) images.push({ src: product.image3, alt: 'Orbit View 3' });
    
    return images.length > 0 ? images : [
      { src: fallbackImages.orbitTop, alt: 'Orbit Top View' },
      { src: fallbackImages.orbitAngle, alt: 'Orbit Angle View' },
      { src: fallbackImages.orbitBottom, alt: 'Orbit Bottom View' }
    ];
  }, [productData, fallbackImages]);

  const getStoreData = useMemo(() => {
    if (!productData?.success || !productData?.products?.store) return [];
    return productData.products.store;
  }, [productData]);

  const product = useMemo(() => productData?.success ? productData.products : null, [productData]);

  const handleOpenWhereToBuyModal = useCallback(() => setIsWhereToBuyModalOpen(true), []);
  const handleCloseWhereToBuyModal = useCallback(() => setIsWhereToBuyModalOpen(false), []);

  const loading = productLoading || specsLoading;
  const error = productError || specsError;

  if (loading) return (
    <div className={styles.pageWrapper}>
      <div className={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className={styles.pageWrapper}>
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <HeroSection
          subtitle={product?.title || "axlOrbit"}
          title={product?.banner_heading || "Flawless Positioning. Unmatched Reliability"}
          gradientFrom="#F97738"
          gradientTo="#FCB999"
          showBottomFade={false}
        />
      </section>

      {/* Orbit Overlap Section */}
      <div className={styles.orbitWrapper}>
        <img
          id="orbitImage"
          src={product?.banner_image ? `${CMS_BASE_URL}/attachments/${product.banner_image}` : fallbackImages.orbitMain}
          alt="AXL Orbit GPS Module"
          className={styles.orbitImage}
          width="400"
          height="300"
          loading="eager"
        />
      </div>

      {/* Main Content */}
      <div className={styles.mainContainer}>
        {/* Description */}
        <section className={styles.descriptionSection}>
          <h2>
            {product?.description || 
              "The axl Orbit GPS provides ultra-precise positioning with up to 1.5-meter accuracy, maximum position availability through 4 concurrent GNSS receptions, advanced spoofing and jamming detection, and exceptional heading accuracy of 0.3 degrees."
            }
          </h2>
        </section>

        {/* Where to Buy Button */}
        {getStoreData.length > 0 && (
          <section className={styles.whereToBuySection}>
            <CustomButton
              onClick={handleOpenWhereToBuyModal}
              size="large"
              ariaLabel="Where to Buy"
            >
              Where to Buy
            </CustomButton>
          </section>
        )}

        {/* Controller Features */}
        {getFeaturesData.length > 0 && (
          <ControllerFeatures
            mainImage={product?.image1 ? `${CMS_BASE_URL}/attachments/${product.image1}` : fallbackImages.orbitSide}
            mainImageAlt="Orbit Side View"
            additionalImages={getAdditionalImages}
            features={getFeaturesData}
          />
        )}

        {/* Certifications with Security Text */}
        <div className={styles.certificationsWrapper}>
          <Certifications description={false}/>
          <div className={styles.securityContainer}>
            <p>
              axl Orbit-N delivers ultra-precise positioning, exceptional heading accuracy, multi-
              constellation GNSS support, and advanced protection against spoofing and jamming for 
              unmatched reliability.
            </p>
          </div>
        </div>        

        {/* Where to Buy Modal */}
        <Modal isOpen={isWhereToBuyModalOpen} onClose={handleCloseWhereToBuyModal} title="Where to Buy">
          <WhereToBuy storeData={getStoreData} />
        </Modal>

        {/* Full Width Sections */}
        {getBVLOSOperationsData.length > 0 && (
          <BVLOSOperations 
            operations={getBVLOSOperationsData} 
            title={product?.designed_for_title || 'Designed For BVLOS Operations'}
          />
        )}

        {/* Specifications and Downloads */}
        <div className={styles.specificationWrapper}>
          {specificationsData.map((specProduct, index) => (
            <React.Fragment key={specProduct.slug || index}>
              <DroneSpecifications 
                droneImage={specProduct.image ? `${CMS_BASE_URL}/attachments/${specProduct.image}` : fallbackImages.orbitMain}
                specifications={specProduct.specifications || []}
                title={specProduct.title}
                description={specProduct.description}
              />
              
              <DownloadComponent 
                brochure={specProduct.brochure ? `${CMS_BASE_URL}/attachments/${specProduct.brochure}` : null}
              />
            </React.Fragment>
          ))}
        </div>

        {/* Versions Section - Moved to Last */}
        <section className={styles.versionsSection}>
          <h4>Available in two versions</h4>
          <div className={styles.versionsList}>
            <div className={styles.versionItem}>Axl Orbit M10</div>
            <div className={styles.versionItem}>Axl Orbit M10 N</div>
            <div className={styles.versionNote}># No cover option for customizations</div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AxlOrbit;